import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Target, Newspaper, Share2, Users, Video,
  PenLine, Palette, Kanban, Settings as SettingsIcon, UsersRound,
  Bell, Activity, LogOut, Sparkles, Menu, Briefcase, Scissors, Search,
} from "lucide-react";
import FilterBar from "@/components/FilterBar";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/manager", label: "Marketing Manager", icon: Briefcase },
  { to: "/goals", label: "Goal Setting", icon: Target },
  { to: "/pr", label: "PR", icon: Newspaper },
  { to: "/social", label: "Social Media", icon: Share2 },
  { to: "/seo", label: "SEO", icon: Search },
  { to: "/influencer", label: "Influencer Marketing", icon: Users },
  { to: "/video", label: "Video Production", icon: Video },
  { to: "/video-editing", label: "Video Editing", icon: Scissors },
  { to: "/writing", label: "Content Writing", icon: PenLine },
  { to: "/design", label: "Graphic Design", icon: Palette },
  { to: "/pipeline", label: "Content Pipeline", icon: Kanban },
  { to: "/team", label: "Team Master List", icon: UsersRound },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function Layout({ children, onAskAi }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-navy text-white">
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={() => setMobileOpen(false)} data-testid="sidebar-overlay" />
      )}
      <aside className={`w-[250px] shrink-0 border-r border-surface-hover bg-surface flex flex-col fixed h-screen z-30 transition-transform duration-200 lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center gap-3 px-6 h-16 border-b border-surface-hover">
          <div className="w-9 h-9 rounded-lg bg-gold flex items-center justify-center">
            <Activity className="w-5 h-5 text-navy" />
          </div>
          <span className="font-heading text-lg font-bold">PulseBoard</span>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${to.replace("/", "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-gold text-navy font-semibold"
                    : "text-slatetext hover:bg-surface-hover hover:text-white"
                }`
              }
            >
              <Icon className="w-[18px] h-[18px]" /> {label}
            </NavLink>
          ))}
        </nav>
        <button
          data-testid="ai-assistant-nav"
          onClick={() => { onAskAi(); setMobileOpen(false); }}
          className="mx-3 mb-3 flex items-center gap-2 px-3 py-2.5 rounded-md bg-navy border border-gold/40 text-gold hover:bg-gold/10 transition-colors duration-200 text-sm font-medium"
        >
          <Sparkles className="w-4 h-4" /> AI Assistant
        </button>
      </aside>

      <div className="flex-1 lg:ml-[250px] flex flex-col min-h-screen w-full">
        <header className="h-16 border-b border-surface-hover bg-surface/60 backdrop-blur flex items-center justify-between px-4 sm:px-8 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slatetext hover:text-white transition-colors duration-200" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6" />
            </button>
            <FilterBar />
          </div>
          <div className="flex items-center gap-5">
            <button data-testid="notification-bell" className="relative text-slatetext hover:text-white transition-colors duration-200">
              <Bell className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#DC2626]" />
            </button>
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-surface-hover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gold text-navy flex items-center justify-center text-sm font-bold">
                  {user?.name?.[0] || "U"}
                </div>
              )}
              <div className="hidden md:block leading-tight">
                <div className="text-sm font-medium">{user?.name}</div>
                <div className="text-xs text-slatetext">{user?.email}</div>
              </div>
              <button data-testid="logout-button" onClick={logout} className="text-slatetext hover:text-[#DC2626] transition-colors duration-200">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-8 animate-fade-in">{children}</main>
      </div>
    </div>
  );
}
